// TagsPicker — select-only tag picker: an input-shell trigger (summary +
// chevron) expanding to a panel with a search box and checkbox rows. Fully
// controlled — the tag library and selected ids come in as props (no
// self-fetching, no inline creation; tag management lives elsewhere).

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Dropdown from './Dropdown';
import Icon from './Icon';
import Input from './Input';

export interface TagsPickerTag {
  id: string;
  title: string;
}

export interface TagsPickerProps {
  tags: TagsPickerTag[];
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  /** Trigger text while nothing is selected. */
  placeholder: string;
  disabled?: boolean;
}

const TagsPicker = ({
  tags,
  selectedIds,
  onChange,
  placeholder,
  disabled = false,
}: TagsPickerProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const q = search.trim().toLowerCase();
  const shown = q ? tags.filter((tag) => tag.title.toLowerCase().includes(q)) : tags;

  const toggle = (id: string) =>
    onChange(selectedIds.includes(id) ? selectedIds.filter((x) => x !== id) : [...selectedIds, id]);

  return (
    <Dropdown
      open={open}
      onClose={() => setOpen(false)}
      align='start'
      className='tags-picker'
      trigger={
        <button
          type='button'
          className={`tags-picker__trigger${open ? ' is-open' : ''}`}
          aria-expanded={open}
          disabled={disabled}
          onClick={() => {
            setOpen((o) => !o);
            setSearch('');
          }}
        >
          <Icon name='tag' size={18} className='tags-picker__trigger-icon' />
          <span
            className={`tags-picker__summary${selectedIds.length === 0 ? ' is-placeholder' : ''}`}
          >
            {selectedIds.length === 0
              ? placeholder
              : t('tags_picker_selected', { count: selectedIds.length })}
          </span>
          <span className='tags-picker__caret' aria-hidden='true'>
            <Icon name={open ? 'chevron-up' : 'chevron-down'} size={15} />
          </span>
        </button>
      }
    >
      <div className='tags-picker__panel'>
        <Input
          size='sm'
          iconStart='search'
          placeholder={t('tags_picker_search')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div className='tags-picker__list'>
          {shown.length === 0 ? (
            <span className='tags-picker__empty'>{t('tags_picker_no_results')}</span>
          ) : (
            shown.map((tag) => {
              const checked = selectedIds.includes(tag.id);
              return (
                <label key={tag.id} className={`tags-picker__row${checked ? ' is-selected' : ''}`}>
                  <input
                    type='checkbox'
                    className='app-checkbox__input'
                    checked={checked}
                    onChange={() => toggle(tag.id)}
                  />
                  <span className='app-checkbox__box' aria-hidden='true'>
                    <Icon name='check' size={14} className='app-checkbox__check' />
                  </span>
                  <Icon name='tag' size={15} className='tags-picker__row-icon' />
                  <span className='tags-picker__row-name'>{tag.title}</span>
                </label>
              );
            })
          )}
        </div>
      </div>
    </Dropdown>
  );
};

export default TagsPicker;
